import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Bold, ChevronDown, ChevronUp } from "lucide-react";
import useRequireAuth from "../../../../utils/useRequireAuth";
import { BASE_URL, CUSTOMER_NOTES_ADD_UPDATE } from "../../../../utils/constants";
import { Avatar } from "@mui/material";
import {
  getFormData,
  getHexWithOpacity,
  getInitials,
  getTitleCase,
} from "../../../../utils/utilityFunctions/stringUtility";
import { getFormatDate } from "../../../../utils/utilityFunctions/dateUtility";
import { saveNotes } from "../../../../utils/customer/customerNotesSlice";
import { ReactComponent as CrossIconAdvancedFilter } from "../../../../assets/images/crossIconAdvancedFilter.svg";
import ToolTipComponent from "../../../UniversalComponents/ToolTipComponent";
import pinIcon from "../../../../assets/images/icons/PinedSolidIcon.svg";
import unpinIcon from "../../../../assets/images/icons/UnPinedSolidIcon.svg";
import { ReactComponent as PinIcon } from "../../../../assets/images/icons/TitlePin.svg";
import { ReactComponent as NoNotesIcon } from "../../../../assets/images/NoNotesIcon.svg";
import { ReactComponent as Attachment } from "../../../../assets/images/icons/Attachment.svg";
import { ReactComponent as PreviewIcon } from "../../../../assets/images/icons/Preview.svg";
import { ReactComponent as EditUnderLine } from "../../../../assets/images/icons/EditUnderLine.svg";
import { ReactComponent as NotesView } from "../../../../assets/images/icons/NotesView.svg";
import { hover, motion } from "framer-motion";
import AvatarName from "../../../UniversalComponents/AvatarName";
import { usePostApiMutation } from "../../../../utils/customer/customerTaskTable";
import { pushAlert } from "../../../../utils/alertSlice";
import { hideLoader, showLoader } from "../../../../utils/loaderSlice";
import CustomDrawer from "../../../UniversalComponents/CustomDrawer";
import MotionSearchBox from "../../../UniversalComponents/MotionSearchBox";
import AddButton from "../../../UniversalComponents/AddButton";
import withPortalHOC from "../../../UniversalComponents/WithPortalHOC";
import List from "../../../UniversalComponents/ListComponent";
import { TagOptionsColored } from "../../../../utils/utilityFunctions/stringUtility";
import moment from "moment";
import DatePickerComponent from "../../../UniversalComponents/DatePickerComponent";
import { ReactComponent as DownArrow } from "../../../../assets/images/downArrow.svg";
import { ReactComponent as LatestPinUnpin } from "../../../../assets/images/icons/LatestPinUnpin.svg";

const highlightTextInHtml = (html, highlight) => {
  if (!highlight.trim()) return html;

  const regex = new RegExp(`(${highlight})`, "gi");
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, null, false);
  let node;
  while ((node = walker.nextNode())) {
    const text = node.textContent;
    if (regex.test(text)) {
      const span = document.createElement("span");
      span.innerHTML = text.replace(regex, (match) => `<span class="bg-yellow-300">${match}</span>`);
      node.replaceWith(span);
    }
  }

  return tempDiv.innerHTML;
};

const PararagraphComponent = ({ CategoryData, searchTerm }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const paragraphRef = useRef(null);

  useEffect(() => {
    const checkHeight = () => {
      if (paragraphRef.current) {
        const lineHeight = parseInt(window.getComputedStyle(paragraphRef.current).lineHeight);
        const height = paragraphRef.current.scrollHeight;

        const maxHeight = lineHeight * 3;

        setShowReadMore(height > maxHeight);
      }
    };

    checkHeight();

    const resizeObserver = new ResizeObserver(checkHeight);
    if (paragraphRef.current) {
      resizeObserver.observe(paragraphRef.current);
    }

    return () => {
      if (paragraphRef.current) {
        resizeObserver.unobserve(paragraphRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [CategoryData]);

  const handleReadMoreClick = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="space-y-2 w-full ">
      <p
        ref={paragraphRef}
        className={`text-sm font-light w-full max-w-[20vw] text-justify text-[#383838] break-words ${isExpanded ? "" : "line-clamp-3 "}`}
        dangerouslySetInnerHTML={{
          __html: highlightTextInHtml(CategoryData, searchTerm),
        }}
      ></p>

      {showReadMore && (
        <button
          className="text-blue-500 hover:text-blue-600 font-300 text-xs cursor-pointer"
          onClick={(e) => handleReadMoreClick(e)}
        >
          {isExpanded ? "Read less" : "Read more"}
          {/* View Full Note */}
        </button>
      )}
    </div>
  );
};

const NotesContentPreivew = ({
  notesData,
  refetchNotesData,
  searchTerm = "",
  handleEditNote = () => null,
  HandlePreviewData,
  isEditable = false,
  sectionType = "Tracking",
  selectedDate,
  setSelectedDate,
  selectedContent,
  setSelectedContent,
  selectedList,
  setSelectedList,
  isCreateTask,
  setIsCreateTask = () => null,
  handleCreateTask,
  DaysToFollowUp = [],
  showAddTodohub,
  isCustomModule = false,
  Item,
}) => {
  const [loading, setLoading] = useState(false);
  const { token } = useRequireAuth();
  const dispatch = useDispatch();
  // const [contentCount, setContentCount] = useState(10);
  //const [searchTerm, setSearchTerm] = useState("");
  const [expandedNotes, setExpandedNotes] = useState({});
  const containerRef = useRef(null);
  const companyLocalData = localStorage.getItem("company_data");
  const parsedCompanyData = JSON.parse(companyLocalData);
  const [color, setColor] = useState(null);
  // const [pinnedNotes, setPinnedNotes] = useState([]);
  // const [pinnedCount, setPinnedCount] = useState(0);
  const [isPinnedVisible, setIsPinnedVisible] = useState(false);

  const predefinedColors = [
    "#6417D8",
    "#008937",
    "#9A0D94",
    "#EE950F",
    "#00969F",
    "#BC1203",
    "#5F5E52",
    "#EF177F",
    "#164A79",
    "#6417D8",
    "#310082",
    "#820002",
    "#007FFF",
    "#0E8BFE",
    "#686868",
    "#266B89",
    "#3FB62C",
    "#1A00E1",
    "#35281D",
    "#B74F6F",
    "#F16136",
    "#879300",
    "#56003F",
    "#560001",
    "#9B6F00",
    "#006994",
  ];
  const [
    AddUpdateNotes,
    {
      data: NotesAddUpdateData,
      isError: NotesAddUpdateError,
      isSuccess: NotesAddUpdateSuccess,
      isLoading: NotesAddUpdateLoading,
    },
  ] = usePostApiMutation();

  const getFileLastName = (filePath) => {
    if (filePath && typeof filePath === "string" && filePath.includes("/")) {
      return filePath.split("/").pop();
    } else {
      return filePath;
    }
  };

  const PortalList = withPortalHOC(List);
  const to_doRef = useRef(null);
  const dateRef = useRef(null);
  const listRef = useRef(null);

  const [showViewList, setShowViewList] = useState(false);
  const [DateViewContent, setDateViewContent] = useState(false);
  const [ListViewContent, setListViewContent] = useState(false);
  const [isCustomDateView, setIsCustomDateView] = useState(false);
  const [isAddToDoChecked, setIsAddToDoChecked] = useState(false);

  const handleCheckboxChange = (e) => {
    setIsCreateTask(e.target.checked);
    e.target.blur();
  };

  const handleSelectContent = (selected) => {
    setSelectedContent(selected?.value); // store only value
    setShowViewList(false);
  };
  const TaskToDoData = [
    { label: "Task", value: "Task" },
    { label: "Deal", value: "Deal" },
    { label: "Lead", value: "Lead" },
    { label: "Job", value: "Job" },
  ];

  const OptionData = [
    { label: "Wild Card C. Inc <Task name 1>", value: "Wild Card C. Inc <Task name 1>" },
    { label: "Wild Card C. Inc <Task name 2>", value: "Wild Card C. Inc <Task name 2>" },
    { label: "Wild Card C. Inc <Task name 3>", value: "Wild Card C. Inc <Task name 3>" },
    { label: "Wild Card C. Inc <Task name 4>", value: "Wild Card C. Inc <Task name 4>" },
  ];

  const handleCustomViewClick = () => {
    setDateViewContent(false);
    setIsCustomDateView(true);
  };

  // const DaysToFollowUp = Array.from({ length: 2 }).map((_, idx) => {
  //   const actualIndex = idx + 1;
  //   const date = moment().add(actualIndex, "days");
  //   const formattedDate = date.format("DD MMM YYYY");

  //   const dayLabel = actualIndex === 1 ? "Tomorrow" : "Day After";

  //   return {
  //     label: `${dayLabel} (${formattedDate})`,
  //     value: dayLabel,
  //     date: formattedDate,
  //   };
  // });

  const selectedDateOption = DaysToFollowUp.find((item) => item?.value === selectedDate);
  console.log(selectedDateOption, "selectedDateOption");

  const handleSelectDate = (selectedOption) => {
    setSelectedDate(selectedOption?.value); // { label, value }
  };

  const handleDateChange = (date) => {
    const formatted = moment(date).format("DD MMM YYYY");
    setSelectedDate(formatted);
    // setIsCustomDateView(false);
  };

  useEffect(() => {
    //loading
    if (NotesAddUpdateLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
    //try block
    if (NotesAddUpdateSuccess && NotesAddUpdateData.success) {
      refetchNotesData();
    }

    // catch block
    if (NotesAddUpdateError) {
      dispatch(pushAlert({ message: "something went wrong", type: "danger", visible: true }));
    }
  }, [NotesAddUpdateLoading, NotesAddUpdateSuccess, NotesAddUpdateError]);

  const downloadFile = async (file) => {
    const url = `${file}`;

    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleExpand = (id) => {
    setExpandedNotes((prevExpanded) => ({
      ...prevExpanded,
      [id]: !prevExpanded[id],
    }));
  };

  console.log(notesData, "vinaynotesdata", searchTerm);
  const filteredNotes = notesData?.filter(
    (note) =>
      note?.notes_title?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      note?.notes_body?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      note?.category_name?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  console.log(filteredNotes, "vinaynotesdata");

  const pinnedNotesData = filteredNotes.filter((note) => note.is_pinned === true);

  // useEffect(() => {
  //   setPinnedNotes(pinnedNotesData);

  //   setPinnedCount(pinnedNotesData.length);
  // }, [filteredNotes]);

  const isContentLong = (content) => {
    const testElement = document.createElement("div");
    testElement.style.position = "absolute";
    testElement.style.visibility = "hidden";
    testElement.style.height = "auto";
    testElement.style.width = "300px";
    testElement.style.lineHeight = "20px";
    testElement.innerText = content;
    document.body.appendChild(testElement);
    const isLong = testElement.clientHeight > 70;
    document.body.removeChild(testElement);
    return isLong;
  };

  const handlePinned = async (note) => {
    const notesData = {
      notes_category_id: note.notes_category_id,
      colour_code: note.colour_code,
      notes_title: note.notes_title,
      company_id: parsedCompanyData.company_id,
      notes_body: note.notes_body,
      rag_status: "wip",
      id: note.id,
      is_pinned: !Boolean(note.is_pinned),
    };
    const PinUnpinNote = getFormData(notesData);

    try {
      const response = await AddUpdateNotes({
        data: notesData,
        url: `${CUSTOMER_NOTES_ADD_UPDATE}?${isCustomModule ? `template_id=${Item?.template_id}&row_id=${Item?.row_id}` : `type=${sectionType}`}`,
      });
      if (response?.success || response?.data?.success || response?.data?.status) {
        dispatch(
          pushAlert({
            message: !Boolean(note.is_pinned) ? "Note pinned successfully." : "Note unpinned successfully.",
            type: "success",
            visible: true,
          })
        );
      }
    } catch (error) {
      dispatch(hideLoader());
      dispatch(
        pushAlert({
          message: error.message || "Error updating pin status",
          type: "danger",
          visible: true,
        })
      );
    }
  };

  const handleCheckboxAdd = (id, isAddToDoChecked) => {
    setIsAddToDoChecked(isAddToDoChecked ? id : null);
  };

  const handleClickScroll = (note) => {
    const noteElement = document.getElementById(`sidenote-${note.id}`);

    if (noteElement) {
      noteElement.scrollIntoView({ behavior: "smooth", block: "center" });
      noteElement.classList.add("animate-zoom-in", "border-2", "border-[#50C878]", "p-2", "rounded-[5px]");
      if (isContentLong(note.body)) {
        toggleExpand(note.id);
      }

      setTimeout(() => {
        noteElement.classList.remove("border-2", "border-[#50C878]", "p-2", "rounded-[5px]");
        noteElement.classList.add("transition-all", "duration-600", "opacity-100");
      }, 2000);
    }
  };
  console.log(filteredNotes, "filteredNotesfilteredNotes");
  return (
    <>
      <div
        className="flex items-center justify-center flex-col overflow-auto w-full h-[70vh] border-l-4 rounded-sm"
        // style={{ borderLeftColor: `${filteredNotes[0]?.colour_code}` }}
      >
        {/* <div className="w-full flex items-center justify-between border-b py-3 px-5">
          <span className="text-[#666]">Notes Preview</span>
 
          <button
           className="text-[#D2DAE1] w-7 h-7 ms-auto flex justify-center items-center hover:bg-[#F3F5F7] transition-colors duration-200"
            data-modal-hide="assets-modal"
          >
"
            onClick={() => {
              HandlePreviewData();
            }}
         
            <CrossIconAdvancedFilter />
          </button>
        </div> */}
        <div className="w-full flex items-center justify-between border-b py-3 px-5">
          <span className="text-[#666]">Notes Preview</span>

          <button
            className="text-[#D2DAE1] w-7 h-7 ms-auto flex justify-center items-center hover:bg-[#F3F5F7] transition-colors duration-200"
            data-modal-hide="assets-modal"
            onClick={HandlePreviewData}
          >
            <CrossIconAdvancedFilter />
          </button>
        </div>

        {
          // notes Content Preview
          <div className="flex-1 w-full min-h-0 grow mt-2">
            <div className="flex-1 h-full  pr-1 notesscrollbar">
              {notesData.length > 0 ? (
                filteredNotes.length > 0 ? (
                  (console.log(filteredNotes, "filteredNotesfilteredNotes"),
                  filteredNotes?.map((note, idx) => (
                    <>
                      <div className="flex items-start justify-between flex-col w-full h-full">
                        <div className="flex flex-col w-full min-h-0 grow" key={note.id} id={`sidenote-${note.id}`}>
                          <div className=" bg-white px-2 rounded-lg  h-full">
                            <div className=" flex items-start flex-col justify-centre p-1 h-full w-full">
                              <div
                                className=" mt-1 flex items-center justify-between w-full gap-x-2 rounded-md px-2 py-1"
                                style={{ backgroundColor: getHexWithOpacity(filteredNotes[0]?.colour_code, 15) }}
                              >
                                <AvatarName
                                  name={getInitials("vinay")}
                                  size="medium"
                                  bgColor={"#e8ecf299"}
                                  textColor={filteredNotes[0]?.colour_code}
                                  borderColor={filteredNotes[0]?.colour_code}
                                />
                                <div className="flex justify-between w-full">
                                  <div className="flex items-start gap-x-1">
                                    <div className="">
                                      <div
                                        className=" text-base font-semibold capitalize pb-[1px] truncate w-72"
                                        style={{ color: filteredNotes[0]?.colour_code }}
                                        dangerouslySetInnerHTML={{
                                          __html: highlightTextInHtml(note?.notes_title, searchTerm),
                                        }}
                                      />
                                      <div className="flex items-center justify-start gap-x-2">
                                        <div className="text-[#767676] text-xs font-300 capitalize pr-1">
                                          {note?.created_by}
                                        </div>
                                        <span className="h-3 w-[1px] bg-[#78787894]"></span>

                                        <div className="text-[#767676] text-xs font-300 capitalize">
                                          {getFormatDate(note?.created_at)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-x-2  cursor-pointer">
                                    {Boolean(isEditable) && (
                                      <EditUnderLine
                                        onClick={handleEditNote}
                                        className="mr-1 fill-[#969ba9] hover:fill-black "
                                      />
                                    )}

                                    {Boolean(isEditable) && (
                                      // <span onClick={() => handlePinned(note)} className="hover:cursor-pointer me-1 group-hover:fill-black">
                                      //   {" "}
                                      //   <img src={Boolean(note.is_pinned) ? pinIcon : unpinIcon} alt="Pin ICon" />
                                      // </span>
                                      <ToolTipComponent>
                                        <span
                                          onClick={() => handlePinned(note)}
                                          className="cursor-pointer me-1 group inline-block"
                                        >
                                          <LatestPinUnpin
                                            className={`w-4 h-4 transition-colors duration-200 ${
                                              Boolean(note?.is_pinned)
                                                ? "fill-[#00BA00] group-hover:fill-[#30a930]"
                                                : "fill-[#8E8E8E] group-hover:fill-black"
                                            }`}
                                          />
                                        </span>
                                      </ToolTipComponent>
                                    )}

                                    <div className="flex items-center justify-evenly border border-[#D2DAE1] rounded m-1 px-2 py-1">
                                      <div
                                        className=" text-sm font-400"
                                        dangerouslySetInnerHTML={{
                                          __html: highlightTextInHtml(note?.category_name, searchTerm),
                                        }}
                                      ></div>
                                      {/* <i
                                      className="bi bi-circle-fill text-[10px] "
                                      style={{ color: note.colour_code || "#000" }}
                                    ></i> */}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-start justify-between min-h-0 grow flex-col w-full p-2">
                                <div className="flex items-start w-full noteViewPad min-h-0 grow overflow-y-auto">
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: highlightTextInHtml(note?.notes_body, searchTerm),
                                    }}
                                  ></div>
                                </div>
                                <div className="gap-2 w-full flex items-centre justify-end flex-wrap-reverse">
                                  {
                                    note?.attachments?.length > 0 &&
                                      note?.attachments.map((attachment) => (
                                        <div
                                          key={attachment.id}
                                          className="flex items-start gap-2 border p-2 rounded h-9"
                                        >
                                          <span className="truncate max-w-[200px]">{attachment.file_name}</span>
                                          <button
                                            className="text-blue-500 hover:text-blue-700"
                                            onClick={() => downloadFile(attachment.view_url)}
                                          >
                                            <i className="bi bi-eye text-14"></i>
                                          </button>
                                          <button
                                            className="text-blue-500 hover:text-blue-700"
                                            onClick={() => downloadFile(attachment.download_url)}
                                          >
                                            <i className="bi bi-download text-12"></i>
                                          </button>
                                        </div>
                                      ))

                                    // <div className="flex items-center justify-center w-7 h-7 border rounded-lg bg-[#F0F0F0] border-[#D2DAE1]">
                                    //   <Attachment />
                                    // </div>
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-x-2 gap-y-2 my-2 mx-4">
                          {note?.tags?.map((option) => {
                            return (
                              <span
                                key={option?.value}
                                className="text-white text-xs font-medium px-2 py-1 rounded items-center"
                                style={{
                                  backgroundColor: getHexWithOpacity(option?.color_code, 10) || "#666",
                                  color: option?.color_code,
                                  border: `1px solid ${option?.color_code}`,
                                }}
                              >
                                {option?.label}
                                {/* <button type="button" className="ml-2 text-white">
                                  <i className="bi bi-x"></i>
                                </button> */}
                              </span>
                            );
                          })}
                        </div>

                        {/* Uncomment for task Create in Notes */}

                        {/* {Boolean(isEditable) one time false*/}
                        {false && (
                          <div className="flex justify-between  items-center  w-full bg-[#e9edf070]">
                            <div
                              className={`flex items-center gap-2  ml-4 rounded-md ${isCreateTask ? "opacity-100" : "opacity-70 cursor-not-allowed"}`}
                            >
                              <input
                                type="checkbox"
                                className=" h-4 w-4 rounded border-gray-400 bg-white text-[#2999BC] focus:ring-0 focus:outline-none cursor-pointer"
                                id="to-do-data"
                                name="to_do_data"
                                onChange={handleCheckboxChange}
                                checked={isCreateTask}
                              />
                              <div className="flex items-center text-[#383838] font-normal text-base">
                                Add To-Do Hub -
                                <div className="relative w-fit">
                                  <div
                                    className="text-[#1D4469] text-base font-medium whitespace-nowrap  flex items-center gap-1 bg-white px-2 py-0.5 rounded-md"
                                    ref={to_doRef}
                                  >
                                    <div className="flex items-center gap-2 ">
                                      <span className="text-[#1D4469] font-medium">
                                        {getTitleCase(selectedContent)}
                                      </span>
                                      <div
                                        className={` ${isCreateTask ? "cursor-pointer" : "cursor-not-allowed"}`}
                                        onClick={() => {
                                          if (!isCreateTask) return; // Prevent action if checkbox is not checked
                                          setShowViewList((prev) => !prev);
                                        }}
                                      >
                                        <DownArrow
                                          fill={isCreateTask === true && showViewList ? "#383838" : "#383838"}
                                          className={`transition-transform duration-200 h-3 w-3 rounded-sm  ${isCreateTask === true && showViewList ? "rotate-180 " : "rotate-0 "}`}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  {isCreateTask === true && showViewList && (
                                    <div className="absolute  mt-1  bg-white shadow border rounded-md ">
                                      <PortalList
                                        open={showViewList}
                                        onClose={() => setShowViewList(false)}
                                        containerRef={to_doRef}
                                        options={TaskToDoData}
                                        selectedOptions={[TaskToDoData.find((opt) => opt.value === selectedContent)]}
                                        setSelectedOptions={(selected) => setSelectedContent(selected[0]?.value)}
                                        onOptionClick={(selected) => handleSelectContent(selected[0])}
                                        size="medium"
                                        name="TaskToDoData"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center text-[#383838] text-base font-normal">
                                for
                                <div className="relative ">
                                  <div
                                    className=" py-1 flex items-center justify-center border border-transparent  h-full rounded-md"
                                    ref={dateRef}
                                  >
                                    <span className=" whitespace-nowrap flex items-center gap-2 bg-white px-2 py-0.5 rounded-md">
                                      <span className="text-[#1D4469]  font-medium ">
                                        {/* {selectedDateOption?.label || DaysToFollowUp.find((item) => item.value === selectedDate).label} */}
                                        {selectedDateOption?.label || selectedDate}
                                      </span>
                                      <div
                                        className={` ${isCreateTask ? "cursor-pointer" : "cursor-not-allowed"}`}
                                        onClick={() => {
                                          if (!isCreateTask) return; // Prevent action if checkbox is not checked
                                          setDateViewContent((prev) => !prev);
                                          setIsCustomDateView(false); // close date picker
                                        }}
                                      >
                                        <DownArrow
                                          fill={isCreateTask === true && DateViewContent ? "#383838" : "#383838"}
                                          className={`transition-transform duration-200 h-3 w-3 ${isCreateTask === true && DateViewContent ? "rotate-180" : "rotate-0"}`}
                                        />
                                      </div>
                                    </span>
                                  </div>

                                  <div>
                                    {isCreateTask === true && DateViewContent && (
                                      <div className="absolute top-full mt-1  shadow border rounded-md z-10">
                                        <PortalList
                                          open={DateViewContent}
                                          onClose={() => setDateViewContent(false)}
                                          containerRef={dateRef}
                                          options={DaysToFollowUp}
                                          selectedOptions={[DaysToFollowUp.find((item) => item.value === selectedDate)]}
                                          setSelectedOptions={(selected) => setSelectedDate(selected[0]?.value)}
                                          onOptionClick={(selected) => {
                                            handleSelectDate(selected[0]);
                                            setDateViewContent(false);
                                          }}
                                          size="medium"
                                          name="dateContent"
                                          addButton
                                          addButtonLabel="Custom Date"
                                          addButtonAction={handleCustomViewClick}
                                        />
                                      </div>
                                    )}

                                    {isCustomDateView && (
                                      <div className="">
                                        <DatePickerComponent
                                          dateFormat="dd MMM yyyy"
                                          placeholderText="DD MM YYYY"
                                          showYearDropdown
                                          autoOpen
                                          showMonthDropdown
                                          scrollableYearDropdown
                                          border="hidden"
                                          onChange={handleDateChange}
                                          boxHidden
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <button
                                onClick={() => {
                                  if (!isCreateTask) return;
                                  handleCheckboxAdd(note.id, false);
                                  handleCreateTask(note);
                                }}
                                className={`bg-[#2999BC] text-white px-2 py-0.5 rounded-md ${isCreateTask ? "opacity-100" : "opacity-70 cursor-not-allowed"}`}
                              >
                                Save
                              </button>
                            </div>

                            {/* Uncomment for Link */}
                            {/* <div className={`flex ${isChecked2 ? "opacity-100" : "opacity-70 cursor-not-allowed"} mr-4`}>
                              <div className="relative ml-2">
                                <div
                                  className="py-1 flex items-center justify-center border border-transparent h-full rounded-md"
                                  ref={listRef}
                                >
                                  <span className="whitespace-nowrap flex items-center gap-2 bg-white px-2 py-0.5 rounded-md">
                                    <span className="text-[#1D4469] font-medium">Link ({selectedList.length})</span>
 
                                    <div
                                      className={`${isChecked2 ? "cursor-pointer" : "cursor-not-allowed"}`}
                                      onClick={() => {
                                        if (!isChecked2) return;
                                        setListViewContent((prev) => !prev);
                                      }}
                                    >
                                      <DownArrow
                                        fill="#383838"
                                        className={`transition-transform duration-200 h-3 w-3 ${
                                          isChecked2 && ListViewContent ? "rotate-180" : "rotate-0"
                                        }`}
                                      />
                                    </div>
                                  </span>
                                </div>
 
                                {ListViewContent && (
                                  <div className="absolute top-full mt-1 shadow border rounded-md z-10">
                                    <PortalList
                                      xAxis={-190}
                                      showSearch
                                      open={ListViewContent}
                                      onClose={() => setListViewContent(false)}
                                      containerRef={listRef}
                                      options={OptionData}
                                      selectedOptions={OptionData.filter((item) => selectedList.includes(item.value))}
                                      setSelectedOptions={(selected) => {
                                        const values = selected.map((item) => item.value);
                                        setSelectedList(values);
                                      }}
                                      onOptionClick={(selected) => {
                                        const values = selected.map((item) => item.value);
                                        setSelectedList(values); //  Always update here
                                      }}
                                      multiSelect
                                      size="medium"
                                      name="listContent"
                                      show
                                    />
                                  </div>
                                )}
                              </div>
                            </div> */}
                          </div>
                        )}

                        {/* uncomment for Task Create in notes */}

                        <div className="w-full flex items-center justify-between border-t gap-x-4 py-3 px-3 h-14 overflow-hidden">
                          {/* Left side content */}
                          {/* {showAddTodohub && (
                            <div className="flex gap-2 bg-[#E9EDF0] items-center py-1 rounded-md px-2" key={note.id}>
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded bg-white checked:bg-[#2999BC] text-white focus:ring-0 focus:outline-none cursor-pointer"
                                id="to-do-add-data"
                                name="to_do_add_data"
                                onChange={(e) => {
                                  handleCheckboxAdd(note.id, e.target.checked);
                                  e.target.blur();
                                }}
                                checked={isAddToDoChecked === note.id}
                              />
 
                              <div className="flex items-center text-[#383838]  text-base font-normal">
                                Add as ToDo Hub
                              </div>
                            </div>
                          )} */}

                          {/* <div className="flex items-center justify-start gap-x-1 ">
                                                    <div className="text-[#8E8E8E] text-xs font-500 "> Created By: </div>
                                                    <div className="text-[#8E8E8E] text-xs font-300 ">{note?.created_by}</div>
                                                    <div className="text-[#8E8E8E] text-xs font-300 ">|</div>
                                                    <div className="text-[#8E8E8E] text-xs font-300 ">{note?.created_at}</div>
                                                  </div> */}

                          {/* Right side "Last Updated" - always visible */}
                          <div className="flex items-center justify-end gap-x-1 ml-auto">
                            <div className="text-[#8E8E8E] text-xs font-500">Last Updated:</div>
                            <div className="text-[#8E8E8E] text-xs font-300">{note?.updated_by}</div>
                            <div className="text-[#8E8E8E] text-xs font-300">|</div>
                            <div className="text-[#8E8E8E] text-xs font-300">{note?.updated_at}</div>
                          </div>
                        </div>
                      </div>
                    </>
                  )))
                ) : (
                  <div className="h-[calc(100vh-160px)]">
                    <div className="flex justify-center items-center flex-col space-y-2 h-full ">
                      <NoNotesIcon />
                      {/* <span className="text-14 text-black text-sm font-400">No Notes is Available.</span> */}
                      <span
                        // onClick={handleEditNote}
                        className="border px-5 py-1.5   cursor-pointer items-center text-center text-sm font-400  hover:border-[#1D4469] hover:text-[#1D4469] text-[#383838] rounded-3xl shadow-[0px_1px_0px_0px_rgba(0,0,0,0.10)_inset,0px_0px_4px_0px_rgba(0,0,0,0.15)]"
                      >
                        No matching notes found.
                      </span>
                    </div>
                  </div>
                )
              ) : (
                <div className="h-full w-full flex items-center justify-center">
                  <div className="flex justify-center items-center flex-col space-y-2 h-full ">
                    <NoNotesIcon />
                    {/* <span className="text-14 text-black text-sm font-400">No Notes is Available.</span> */}
                    <span
                      onClick={handleEditNote}
                      className="border px-5 py-1.5   cursor-pointer items-center text-center text-sm font-400  hover:border-[#1D4469] hover:text-[#1D4469] text-[#383838] rounded-3xl shadow-[0px_1px_0px_0px_rgba(0,0,0,0.10)_inset,0px_0px_4px_0px_rgba(0,0,0,0.15)]"
                    >
                      + Add Note
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        }
      </div>
      {loading && (
        <div className="flex my-2 border-b border-[#203C501A]">
          <div className="mr-3">
            <div className="animate-pulse bg-gray-200 rounded-full h-[30px] w-[30px]"></div>
          </div>
          <div>
            <div className="animate-pulse bg-gray-200 h-[16px] w-[100px] mb-1"></div>
            <div className="animate-pulse bg-gray-200 h-[16px] w-[150px]"></div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotesContentPreivew;
